import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  Clock,
  Zap,
  MessageCircle,
  ThumbsUp,
  Camera,
  Send,
  ArrowRight,
  MapPin,
} from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import CtaFinal from "components/cta/CtaFinal";
import { AppButton } from "components/misc/Buttons";
import useInView from "helpers/useInView";
import {
  fadeUpVariants,
  staggerContainerVariants,
  staggerItemVariants,
} from "lib/animations";
import { IMAGES } from "constants/images";
import config from "lib/config";
import { contactService } from "services/contact.service";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Validation schema ────────────────────────────────────────────────────────
const contactSchema = z.object({
  firstName: z.string().min(2, "Prénom requis (min. 2 caractères)"),
  lastName: z.string().min(2, "Nom requis (min. 2 caractères)"),
  email: z.string().email("Email invalide"),
  phone: z.string().optional(),
  service: z.string().min(1, "Veuillez choisir un service"),
  subject: z.string().min(5, "Sujet requis (min. 5 caractères)"),
  message: z.string().min(20, "Message trop court (min. 20 caractères)"),
});
type ContactFormData = z.infer<typeof contactSchema>;

// ─── Styled ──────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 5rem 1.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 3fr;
  }
`;

// ── Info column ──

const InfoCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const InfoEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${C_PRIMARY};
  margin-bottom: 0.75rem;
`;

const InfoTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  line-height: 1.15;
  margin-bottom: 2.5rem;
`;

const ContactCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.5rem 1.5rem 1.5rem 1.75rem;
  border-left: 3px solid ${C_PRIMARY};
  border-bottom: 1px solid #f0f0f0;
  background: white;
  transition: background 0.2s ease;

  &:first-of-type {
    border-top: 1px solid #f0f0f0;
  }

  &:hover {
    background: #fff8f4;
  }
`;

const CardIconWrap = styled.div`
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff4ec;
  color: ${C_PRIMARY};
`;

const ContactCardTitle = styled.p`
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
  margin-bottom: 0.3rem;
`;

const ContactCardValue = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${C_SECONDARY};
  line-height: 1.65;
`;

const SocialSection = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f0f0f0;
`;

const SocialLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SocialBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #f3f4f6;
  color: ${C_SECONDARY};
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  transition:
    background 0.2s ease,
    color 0.2s ease;
  border-left: 2px solid transparent;

  &:hover {
    background: ${C_PRIMARY};
    color: white;
    border-left-color: ${C_SECONDARY};
  }
`;

// ── Offices ──

const OfficesSection = styled.div`
  margin-top: 2rem;
  border-top: 1px solid #f0f0f0;
  padding-top: 1.75rem;
`;

const OfficesLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const OfficeCard = styled.div`
  padding: 1rem;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-left: 3px solid ${C_PRIMARY};
`;

const OfficeCardLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${C_PRIMARY};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const OfficeCardAddress = styled.p`
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.65;
`;

// ── Form column ──

const FormCol = styled.div``;

const FormCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 2.5rem;
`;

const FormEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${C_PRIMARY};
  margin-bottom: 0.5rem;
`;

const FormTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 2rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FormGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-5`;
const FormGroup = tw.div`flex flex-col gap-2`;
const FormGroupFull = tw(FormGroup)`sm:col-span-2`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: ${(p) => (p.$hasError ? "#fef2f2" : "#fafafa")};
  font-size: 0.9rem;
  color: ${C_SECONDARY};
  outline: none;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
    background: white;
  }

  &::placeholder {
    color: #c1c1c1;
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: #fafafa;
  font-size: 0.9rem;
  color: ${C_SECONDARY};
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
    background: white;
  }
`;

const Textarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: ${(p) => (p.$hasError ? "#fef2f2" : "#fafafa")};
  font-size: 0.9rem;
  color: ${C_SECONDARY};
  outline: none;
  resize: vertical;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
    background: white;
  }

  &::placeholder {
    color: #c1c1c1;
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  color: #ef4444;
`;

// ─── Styles des boutons (remplacés par AppButton) ─────────────────────────────
// SubmitBtn supprimé — utiliser <AppButton variant="primary" fullWidth>

// ─── Contact info data ────────────────────────────────────────────────────────
const CONTACT_ITEMS = [
  {
    icon: <Mail size={18} />,
    title: "Email",
    value: config.contactEmail,
  },
  {
    icon: <Phone size={18} />,
    title: "Téléphone France",
    value: `${config.phonesFrance}\nDisponible 7j/7 · 8h – 22h`,
  },
  {
    icon: <Phone size={18} />,
    title: "Téléphone Cameroun",
    value: `${config.phonesCameroun}\nDisponible 7j/7 · 8h – 22h`,
  },
  {
    icon: <Clock size={18} />,
    title: "Horaires de réponse",
    value: "Lun – Sam : 8h00 – 22h00\nDim : 10h00 – 18h00",
  },
  {
    icon: <Zap size={18} />,
    title: "Délai de réponse moyen",
    value: "Moins de 2h en semaine\nMaximum 12h le week-end",
  },
];

const SOCIAL_ITEMS = [
  {
    icon: <ThumbsUp size={15} />,
    href: config.socialLinks.facebook,
    label: "Facebook",
  },
  {
    icon: <Camera size={15} />,
    href: config.socialLinks.instagram,
    label: "Instagram",
  },
  {
    icon: <MessageCircle size={15} />,
    href: config.socialLinks.whatsapp,
    label: "WhatsApp",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
const ContactPage: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({ resolver: zodResolver(contactSchema) });

  const onSubmit = async (data: ContactFormData) => {
    try {
      await contactService.send({
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        phone: data.phone,
        service: data.service,
        subject: data.subject,
        message: data.message,
      });
      toast.success("Message envoyé ! Nous vous répondons sous 24h.");
      reset();
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    }
  };

  return (
    <LandingLayout disableAnimation>
      <Hero
        backgroundImageSrc={IMAGES.contactImg}
        notification="Contactez-nous"
        heading={
          <>
            <span>Nous sommes là pour</span>
            <br />
            <SlantedBackground>vous aider.</SlantedBackground>
          </>
        }
        description="Une question sur nos services, les tarifs ou votre démarche ? Notre équipe vous répond sous 24h."
      />

      <PageWrapper>
        {/* ── Info column ── */}
        <InfoCol>
          <FadeUpSection>
            <InfoEyebrow>Coordonnées</InfoEyebrow>
            <InfoTitle>Parlez-nous de votre projet.</InfoTitle>
          </FadeUpSection>

          <MDiv
            variants={staggerContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {CONTACT_ITEMS.map((item) => (
              <MDiv key={item.title} variants={staggerItemVariants}>
                <ContactCard>
                  <CardIconWrap>{item.icon}</CardIconWrap>
                  <div>
                    <ContactCardTitle>{item.title}</ContactCardTitle>
                    <ContactCardValue>
                      {item.value.split("\n").map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < item.value.split("\n").length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </ContactCardValue>
                  </div>
                </ContactCard>
              </MDiv>
            ))}
          </MDiv>

          <FadeUpSection delay={0.3}>
            <SocialSection>
              <SocialLabel>Suivez-nous</SocialLabel>
              <SocialRow>
                {SOCIAL_ITEMS.map((s) => (
                  <SocialBtn
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                  >
                    {s.icon}
                    {s.label}
                  </SocialBtn>
                ))}
              </SocialRow>
            </SocialSection>
          </FadeUpSection>

          {/* ── Bureaux ── */}
          <FadeUpSection delay={0.4}>
            <OfficesSection>
              <OfficesLabel>Nos bureaux</OfficesLabel>
              <OfficeGrid>
                {Object.values(config.offices).map((office) => (
                  <OfficeCard key={office.label}>
                    <OfficeCardLabel>
                      <MapPin size={11} />
                      {office.label}
                    </OfficeCardLabel>
                    <OfficeCardAddress>
                      {office.street}
                      <br />
                      {office.city}
                    </OfficeCardAddress>
                  </OfficeCard>
                ))}
              </OfficeGrid>
            </OfficesSection>
          </FadeUpSection>
        </InfoCol>

        {/* ── Form column ── */}
        <FadeUpSection delay={0.15}>
          <FormCol>
            <FormCard>
              <FormEyebrow>Formulaire de contact</FormEyebrow>
              <FormTitle>
                <Send size={18} color={C_PRIMARY} />
                Envoyer un message
              </FormTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      placeholder="Harold"
                      $hasError={!!errors.firstName}
                      {...register("firstName")}
                    />
                    {errors.firstName && (
                      <ErrorMsg>{errors.firstName.message}</ErrorMsg>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      placeholder="Brian"
                      $hasError={!!errors.lastName}
                      {...register("lastName")}
                    />
                    {errors.lastName && (
                      <ErrorMsg>{errors.lastName.message}</ErrorMsg>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="jean@example.com"
                      $hasError={!!errors.email}
                      {...register("email")}
                    />
                    {errors.email && (
                      <ErrorMsg>{errors.email.message}</ErrorMsg>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="phone">Téléphone / WhatsApp</Label>
                    <Input
                      id="phone"
                      placeholder="+237 600 00 00 00"
                      {...register("phone")}
                    />
                  </FormGroup>

                  <FormGroupFull>
                    <Label htmlFor="service">Service concerné *</Label>
                    <Select
                      id="service"
                      $hasError={!!errors.service}
                      {...register("service")}
                    >
                      <option value="">-- Choisissez un service --</option>
                      <option value="avi">Attestation AVI — 75€</option>
                      <option value="visa">Accompagnement Visa — 120€</option>
                      <option value="logement">Logement Étudiant — 150€</option>
                      <option value="billets">Billets d'Avion — 50€</option>
                      <option value="finances">Support Financier — 80€</option>
                      <option value="support">Support 24/7 — 30€/mois</option>
                      <option value="autre">Autre renseignement</option>
                    </Select>
                    {errors.service && (
                      <ErrorMsg>{errors.service.message}</ErrorMsg>
                    )}
                  </FormGroupFull>

                  <FormGroupFull>
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      placeholder="Demande d'information sur l'AVI..."
                      $hasError={!!errors.subject}
                      {...register("subject")}
                    />
                    {errors.subject && (
                      <ErrorMsg>{errors.subject.message}</ErrorMsg>
                    )}
                  </FormGroupFull>

                  <FormGroupFull>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      rows={5}
                      placeholder="Décrivez votre situation et votre besoin en détail..."
                      $hasError={!!errors.message}
                      {...register("message")}
                    />
                    {errors.message && (
                      <ErrorMsg>{errors.message.message}</ErrorMsg>
                    )}
                  </FormGroupFull>
                </FormGrid>

                <AppButton
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  iconRight={<ArrowRight size={17} />}
                  style={{ marginTop: "1.5rem" }}
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
                </AppButton>
              </form>
            </FormCard>
          </FormCol>
        </FadeUpSection>
      </PageWrapper>

      <CtaFinal
        label="Une question ?"
        title="Notre équipe est là pour vous accompagner"
        description="Remplissez le formulaire ci-dessus ou contactez-nous directement pour obtenir une réponse rapide."
      />
    </LandingLayout>
  );
};

export default ContactPage;
